import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { AuthSession } from "../types/auth";

interface AuthContextValue {
  session: AuthSession | null;
  isLoading: boolean;
  refreshSession: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const readSession = async (): Promise<AuthSession | null> => {
  const response = await fetch("/api/auth/session");
  if (!response.ok) {
    return null;
  }

  const payload = await response.json() as { authenticated?: boolean; session?: AuthSession };
  if (!payload.authenticated || !payload.session) {
    return null;
  }

  return payload.session;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = async () => {
    try {
      setSession(await readSession());
    } catch {
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } finally {
      setSession(null);
    }
  };

  useEffect(() => {
    void refreshSession();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        session,
        isLoading,
        refreshSession,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
};
